import React, { useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';

interface CBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  height?: string;
  sheetContainerStyle?: React.CSSProperties;
}

const CBottomSheet: React.FC<CBottomSheetProps> = ({
  isOpen,
  onClose,
  children,
  height = '76vh',
  sheetContainerStyle,
}) => {
  const sheetRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <Overlay onClick={handleOverlayClick}>
      <SheetContainer ref={sheetRef} $height={height} style={sheetContainerStyle}>
        <DragHandle onClick={onClose} />
        <Content>{children}</Content>
      </SheetContainer>
    </Overlay>
  );
};

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const slideUp = keyframes`
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  z-index: 99999;
  animation: ${fadeIn} 0.2s ease-out;
`;

const SheetContainer = styled.div<{ $height: string }>`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: ${({ $height }) => $height};
  max-height: 90vh;
  background: #242526;
  border-radius: 16px 16px 0 0;
  animation: ${slideUp} 0.3s ease-out;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const DragHandle = styled.div`
  width: 40px;
  height: 5px;
  background: rgba(255, 255, 255, 0.4);
  border-radius: 999px;
  margin: 10px auto;
  cursor: pointer;
  flex-shrink: 0;
`;

const Content = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
`;

export default React.memo(CBottomSheet);
